// src/hooks/useLazyTable.js
//
// Shared state + query-param logic for any page using <GlobalTable lazy />.
// Handles pagination, sorting, global keyword search, and per-column menu
// filters (equals / not-equals / less-than / greater-than / contains / date)
// the same way for every list page, so each page only needs to:
//
//   const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable();
//
//   useEffect(() => {
//       setLoading(true);
//       axiosInstance.get('/api/invoices/', { params: queryParams })
//           .then(res => { setData(res.data.results); setTotalRecords(res.data.count); })
//           .finally(() => setLoading(false));
//   }, [queryParams]);
//
//   <GlobalTable lazy totalRecords={totalRecords} lazyParams={lazyParams}
//       onPage={onPage} onSort={onSort} onFilter={onFilter} ... />
//
// The backend side of this contract is `ColumnFilterBackend` (gf/utils/generic_filtering.py)
// plus DRF's OrderingFilter/SearchFilter — see InvoiceViewSet for a reference wiring.

import { useMemo, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { FilterMatchMode } from 'primereact/api';

// PrimeReact matchMode -> Django lookup suffix understood by ColumnFilterBackend
const MATCH_MODE_TO_OP = {
    equals: 'exact',
    notEquals: 'ne',
    contains: 'icontains',
    notContains: 'not_icontains',
    startsWith: 'istartswith',
    endsWith: 'iendswith',
    lt: 'lt',
    lte: 'lte',
    gt: 'gt',
    gte: 'gte',
    dateIs: 'exact',
    dateIsNot: 'ne',
    dateBefore: 'lt',
    dateAfter: 'gt',
};

export const buildLazyQueryParams = (lazyParams) => {
    const { rows, first, sortField, sortOrder, filters } = lazyParams;
    const params = {
        limit: rows,
        offset: first,
        ordering: sortField ? `${sortOrder === -1 ? '-' : ''}${sortField}` : undefined,
        search: filters?.global?.value || undefined,
    };

    Object.entries(filters || {}).forEach(([field, meta]) => {
        if (field === 'global') return;
        const constraint = meta?.constraints?.[0];
        const value = constraint?.value;
        if (value !== null && value !== undefined && value !== '') {
            params[field] = value instanceof Date ? format(value, 'yyyy-MM-dd') : value;
            params[`${field}_op`] = MATCH_MODE_TO_OP[constraint.matchMode] || 'exact';
        }
    });

    return params;
};

export const useLazyTable = ({ rows = 15 } = {}) => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows,
        page: 0,
        sortField: null,
        sortOrder: null,
        filters: {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
    });

    const onPage = useCallback((e) => setLazyParams((p) => ({ ...p, ...e })), []);
    const onSort = useCallback((e) => setLazyParams((p) => ({ ...p, ...e, first: 0 })), []);
    const onFilter = useCallback(
        (e) => setLazyParams((p) => ({ ...p, filters: e.filters, first: 0 })),
        []
    );

    // Stable string dependency so callers can use it directly in a useEffect array
    // instead of remembering to JSON.stringify(lazyParams) themselves.
    const queryParams = useMemo(() => buildLazyQueryParams(lazyParams), [lazyParams]);

    return { lazyParams, setLazyParams, onPage, onSort, onFilter, queryParams };
};

export default useLazyTable;